const getDisplayMessages = (messages) => {
  /**
   * Filter out empty messages first
   * @type {Array<Object>}
   * @description Removes messages with empty content
   */
  const filteredMessages = messages.filter(
    (message) => message.content && message.content.length > 0
  );

  /**
   * Groups messages in proper order: User → Assistant (with tool calls and thoughts)
   * The last assistant message in a sequence is the "main" response, but only if
   * it's followed by a user message or end-of-messages (not by more tool calls).
   * @type {Array<Object>}
   */
  const groupedMessages = (() => {
    const result = [];
    let currentThoughts = [];
    let currentToolCalls = [];

    for (let i = 0; i < filteredMessages.length; i++) {
      const message = filteredMessages[i];

      if (message.role === "user") {
        // If we have collected thoughts and tool calls but no final response,
        // create an incomplete assistant message
        if (currentThoughts.length > 0 || currentToolCalls.length > 0) {
          result.push({
            role: "assistant",
            content: "",
            complete: false,
            thoughts: currentThoughts.length > 0 ? currentThoughts : undefined,
            tool_calls:
              currentToolCalls.length > 0 ? currentToolCalls : undefined,
          });
        }

        // Reset for new conversation
        currentThoughts = [];
        currentToolCalls = [];

        // Add user message
        result.push({ ...message, index: i });
      } else if (message.role === "assistant") {
        // Check if this is the last assistant message in the sequence AND
        // no tool messages follow it (which would indicate it's still thinking)
        const isMainResponse = (() => {
          // Look at what follows this assistant message
          let hasTrailingTools = false;
          let hasLaterAssistant = false;
          for (let j = i + 1; j < filteredMessages.length; j++) {
            if (filteredMessages[j].role === "user") {
              break;
            }
            if (filteredMessages[j].role === "assistant") {
              hasLaterAssistant = true;
              break;
            }
            if (filteredMessages[j].role === "tool") {
              hasTrailingTools = true;
            }
          }
          // It's the main response if there are no later assistant messages
          // AND no trailing tool calls (trailing tools mean more work is expected)
          return !hasLaterAssistant && !hasTrailingTools;
        })();

        if (!isMainResponse) {
          // This is a thinking message, add to thoughts
          currentThoughts.push({ ...message, index: i });
        } else {
          // This is the final response, create complete assistant message
          const currentAssistant = { ...message };

          // Look ahead to collect all remaining tool calls and thoughts
          let j = i + 1;
          while (
            j < filteredMessages.length &&
            filteredMessages[j].role !== "user"
          ) {
            const nextMessage = filteredMessages[j];
            if (nextMessage.role === "tool") {
              currentToolCalls.push({
                ...nextMessage,
                count: currentToolCalls.length + 1,
                index: j,
              });
            } else if (
              nextMessage.role === "assistant" &&
              nextMessage.content.startsWith("Thinking")
            ) {
              currentThoughts.push({ ...nextMessage, index: j });
            }
            j++;
          }

          // Add the complete assistant message
          result.push({
            ...currentAssistant,
            thoughts: currentThoughts.length > 0 ? currentThoughts : undefined,
            tool_calls:
              currentToolCalls.length > 0 ? currentToolCalls : undefined,
          });

          // Reset for next conversation
          currentThoughts = [];
          currentToolCalls = [];
        }
      } else if (message.role === "tool") {
        // Add tool message to current collection
        currentToolCalls.push({
          ...message,
          count: currentToolCalls.length + 1,
          index: i,
        });
      }
    }

    // Handle any remaining incomplete conversation
    if (currentThoughts.length > 0 || currentToolCalls.length > 0) {
      result.push({
        role: "assistant",
        content: "",
        complete: false,
        thoughts: currentThoughts.length > 0 ? currentThoughts : undefined,
        tool_calls: currentToolCalls.length > 0 ? currentToolCalls : undefined,
      });
    }

    return result;
  })();

  return groupedMessages;
};

export default getDisplayMessages;
